import { Button, Flex, Popover, Typography } from "antd";
import { Checkbox } from "@libertify/ui";
import { RowsIcon } from "@phosphor-icons/react";
import { useAntTheme } from "hooks/useAntTheme";

// Generic "which columns are visible" popover, shared by any table that
// offers a toggleable set of optional columns (Experiences, Projects, ...).
// The column list, labels and cap are all page-specific, so they arrive as
// props instead of being imported from a page-local registry.

export type TableFieldsDropdownColumn<K extends string = string> = {
  key: K;
  label: string;
  // false = pinned column the user can't hide.
  removable?: boolean;
};

export type TableFieldsDropdownProps<K extends string = string> = {
  columns: TableFieldsDropdownColumn<K>[];
  isVisible: (key: K) => boolean;
  toggleColumn: (key: K) => void;
  canAddMore: boolean;
  title: string;
  buttonLabel: string;
  hint: string;
};

export const TableFieldsDropdown = <K extends string = string>({
  columns,
  isVisible,
  toggleColumn,
  canAddMore,
  title,
  buttonLabel,
  hint,
}: TableFieldsDropdownProps<K>) => {
  const { theme } = useAntTheme();

  const content = (
    <Flex vertical gap={8} style={{ minWidth: 200 }}>
      <Typography.Text type="secondary" style={{ fontSize: theme.fontSizeSM }}>
        {hint}
      </Typography.Text>
      {columns.map((column) => {
        const checked = isVisible(column.key);
        return (
          <Checkbox
            key={column.key}
            checked={checked}
            // Pinned column is locked on; others lock when the budget is full.
            disabled={column.removable === false || (!checked && !canAddMore)}
            onChange={() => toggleColumn(column.key)}
          >
            {column.label}
          </Checkbox>
        );
      })}
    </Flex>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      title={title}
    >
      <Button
        shape="default"
        size="large"
        icon={<RowsIcon size={16} />}
        style={{
          borderColor: theme.colorBorderSecondary,
          color: theme.colorText,
        }}
      >
        {buttonLabel}
      </Button>
    </Popover>
  );
};
